import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, HeartPulse, KeyRound, Download, Upload, FileDown, Eye, Copy, GitMerge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { maskCPF, formatDate } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';

const EMPTY = { nome: '', data_nascimento: '', rg: '', cpf: '', email: '', telefone: '', tipo_sanguineo: '', alergias: '', remedios_continuos: '', obs: '', criar_acesso: false, senha_acesso: '' };
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export default function Clients() {
  const { user } = useAuth();
  const isManager = ['admin', 'supervisor'].includes(user?.role);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [accessPwd, setAccessPwd] = useState('');
  const [granting, setGranting] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const [keepChoice, setKeepChoice] = useState({});
  const [merging, setMerging] = useState(false);
  // Vendedor pode criar cliente, mas não editar cadastros existentes (somente leitura)
  const formReadOnly = !isManager && !!editing;

  const downloadBlob = (data, filename, type) => {
    const blob = new Blob([data], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const exportClients = async () => {
    try { const r = await api.get('/clients/export', { responseType: 'blob' }); downloadBlob(r.data, 'clientes.xlsx', XLSX_MIME); }
    catch { toast.error('Erro ao exportar'); }
  };
  const downloadTemplate = async () => {
    try { const r = await api.get('/clients-import-template', { responseType: 'blob' }); downloadBlob(r.data, 'modelo_clientes.xlsx', XLSX_MIME); }
    catch { toast.error('Erro ao baixar modelo'); }
  };
  const onImportFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportFileName(file.name);
    setImportFile(file);
  };
  const doImport = async () => {
    if (!importFile) { toast.error('Selecione um arquivo .xlsx'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const r = await api.post('/clients/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.created} cliente(s) importado(s)`);
      if (r.data.skipped) toast.warning(`${r.data.skipped} duplicado(s) ignorado(s)`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} linha(s) ignorada(s)`);
      setImportOpen(false); setImportFile(null); setImportFileName(''); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao importar'); }
    finally { setImporting(false); }
  };

  const load = () => {
    api.get('/clients').then((r) => setClients(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const openNew = () => { setEditing(null); setForm(EMPTY); setAccessPwd(''); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c, criar_acesso: false, senha_acesso: '' }); setAccessPwd(''); setOpen(true); };

  const grantAccess = async () => {
    if (!editing) return;
    setGranting(true);
    try {
      const r = await api.post(`/clients/${editing.client_id}/grant-access`, { senha: accessPwd || undefined });
      toast.success(`Acesso habilitado — login: ${r.data.email} / senha: ${r.data.senha}`);
      setEditing(r.data.client);
      setForm((f) => ({ ...f, ...r.data.client }));
      setAccessPwd('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao habilitar acesso');
    } finally {
      setGranting(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.nome) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing.client_id}`, form);
        toast.success('Cliente atualizado');
      } else {
        await api.post('/clients', form);
        toast.success('Cliente cadastrado');
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/clients/${delTarget.client_id}`); toast.success('Cliente removido'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao remover'); }
    finally { setDelTarget(null); }
  };

  const openDuplicates = async () => {
    setDupOpen(true); setDupLoading(true);
    try {
      const r = await api.get('/clients/duplicates');
      setDupGroups(r.data);
      const init = {};
      r.data.forEach((g, i) => { init[i] = g.clients[0]?.client_id; });
      setKeepChoice(init);
    } catch { toast.error('Erro ao carregar duplicados'); }
    finally { setDupLoading(false); }
  };

  const mergeGroup = async (gi) => {
    const group = dupGroups[gi];
    const keep = keepChoice[gi];
    const remove = group.clients.filter((c) => c.client_id !== keep).map((c) => c.client_id);
    if (!keep || remove.length === 0) { toast.error('Selecione o cadastro a manter'); return; }
    setMerging(true);
    try {
      const r = await api.post('/clients/merge', { keep_id: keep, remove_ids: remove });
      toast.success(r.data.message);
      load();
      openDuplicates();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao mesclar'); }
    finally { setMerging(false); }
  };

  const filtered = clients.filter((c) => `${c.nome} ${c.cpf} ${c.email} ${c.telefone}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Clientes</h2>
          <p className="text-sm text-muted-foreground">Base cadastral com dados pessoais e informações de saúde.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setImportOpen(true)} data-testid="clients-import-button"><Upload className="h-4 w-4 mr-2" /> Importar</Button>
          <Button variant="secondary" onClick={exportClients} data-testid="clients-export-button"><Download className="h-4 w-4 mr-2" /> Exportar</Button>
          {isManager && <Button variant="secondary" onClick={openDuplicates} data-testid="clients-duplicates-button"><Copy className="h-4 w-4 mr-2" /> Duplicados</Button>}
          <Button onClick={openNew} data-testid="clients-new-button"><Plus className="h-4 w-4 mr-2" /> Novo cliente</Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CPF, email..." className="pl-9" data-testid="clients-search" />
        </div>
        {loading ? <p className="text-muted-foreground py-8 text-center">Carregando...</p> : filtered.length === 0 ? (
          <div className="py-12 text-center"><Users className="h-8 w-8 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Nenhum cliente encontrado.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="clients-table">
              <TableHeader><TableRow>
                <TableHead>Nome</TableHead><TableHead>Nascimento</TableHead><TableHead>CPF</TableHead><TableHead>Telefone</TableHead>
                <TableHead>Email</TableHead><TableHead>Tipo sang.</TableHead><TableHead className="w-10"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.client_id}>
                    <TableCell className="font-medium">{c.nome}</TableCell>
                    <TableCell className="text-muted-foreground">{c.data_nascimento ? formatDate(c.data_nascimento) : '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{maskCPF(c.cpf)}</TableCell>
                    <TableCell>{c.telefone || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{c.email || '-'}</TableCell>
                    <TableCell>{c.tipo_sanguineo || '-'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`clients-row-actions-${c.client_id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(c)}>
                            {isManager ? <><Pencil className="h-4 w-4 mr-2" /> Editar</> : <><Eye className="h-4 w-4 mr-2" /> Ver detalhes</>}
                          </DropdownMenuItem>
                          {isManager && <DropdownMenuItem onClick={() => setDelTarget(c)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{formReadOnly ? 'Detalhes do cliente' : (editing ? 'Editar cliente' : 'Novo cliente')}</DialogTitle></DialogHeader>
          <form onSubmit={save}>
            <fieldset disabled={formReadOnly} className="space-y-4 m-0 p-0 border-0 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>Nome completo *</Label><Input value={form.nome} onChange={set('nome')} className="mt-1.5" data-testid="client-nome" /></div>
              <div><Label>Data de nascimento</Label><Input type="date" value={form.data_nascimento || ''} onChange={set('data_nascimento')} className="mt-1.5" data-testid="client-nascimento" /></div>
              <div><Label>CPF</Label><Input value={form.cpf} onChange={set('cpf')} placeholder="000.000.000-00" className="mt-1.5" data-testid="client-cpf" /></div>
              <div><Label>RG</Label><Input value={form.rg} onChange={set('rg')} className="mt-1.5" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={set('email')} className="mt-1.5" /></div>
              <div><Label>Telefone</Label><Input value={form.telefone} onChange={set('telefone')} className="mt-1.5" /></div>
            </div>

            <Accordion type="single" collapsible className="border rounded-lg px-4">
              <AccordionItem value="saude" className="border-0">
                <AccordionTrigger className="text-sm" data-testid="clients-health-accordion"><span className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-primary" /> Informações de saúde (opcional)</span></AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <p className="text-xs text-muted-foreground">Dados sensíveis usados apenas para atendimento em emergências, conforme a LGPD.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label>Tipo sanguíneo</Label><Input value={form.tipo_sanguineo} onChange={set('tipo_sanguineo')} placeholder="Ex: O+" className="mt-1.5" /></div>
                  </div>
                  <div><Label>Alergias</Label><Textarea value={form.alergias} onChange={set('alergias')} rows={2} className="mt-1.5" /></div>
                  <div><Label>Remédios contínuos</Label><Textarea value={form.remedios_continuos} onChange={set('remedios_continuos')} rows={2} className="mt-1.5" /></div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div><Label>Observações</Label><Textarea value={form.obs} onChange={set('obs')} rows={2} className="mt-1.5" /></div>

            {!editing && (
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div><Label className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Criar acesso ao portal</Label><p className="text-xs text-muted-foreground mt-1">Permite o cliente acessar e assinar contratos.</p></div>
                  <Switch checked={form.criar_acesso} onCheckedChange={(v) => setForm({ ...form, criar_acesso: v })} data-testid="client-criar-acesso" />
                </div>
                {form.criar_acesso && (
                  <div><Label>Senha de acesso</Label><Input value={form.senha_acesso} onChange={set('senha_acesso')} placeholder="Mínimo 6 caracteres (padrão: cliente123)" className="mt-1.5" /></div>
                )}
              </div>
            )}
            </fieldset>

            {editing && isManager && (
              <div className="rounded-lg border p-4 space-y-3 mb-2" data-testid="client-portal-access-section">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                  <Label className="font-medium">Acesso ao portal</Label>
                </div>
                {editing.user_id ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="client-access-active">
                    <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)]">Acesso ativo</Badge>
                    O cliente já acessa o portal com o e-mail cadastrado.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Crie um login para o cliente acessar o portal e assinar contratos (requer e-mail cadastrado).</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input value={accessPwd} onChange={(e) => setAccessPwd(e.target.value)} placeholder="Senha (padrão: cliente123)" data-testid="client-access-password" />
                      <Button type="button" onClick={grantAccess} disabled={granting} data-testid="client-grant-access">
                        <KeyRound className="h-4 w-4 mr-2" /> {granting ? 'Habilitando...' : 'Habilitar acesso'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{formReadOnly ? 'Fechar' : 'Cancelar'}</Button>
              {!formReadOnly && <Button type="submit" disabled={saving} data-testid="client-save">{saving ? 'Salvando...' : 'Salvar'}</Button>}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir cliente?</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita. Tem certeza que deseja excluir {delTarget?.nome}?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Importar clientes (Excel .xlsx)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Envie uma planilha Excel (.xlsx) com as colunas: nome, data_nascimento, rg, cpf, email, telefone, tipo_sanguineo, alergias, remedios_continuos, obs.
              Baixe o modelo para preencher corretamente.
            </p>
            <Button variant="secondary" className="w-full" onClick={downloadTemplate} data-testid="clients-template-button">
              <FileDown className="h-4 w-4 mr-2" /> Baixar modelo .xlsx
            </Button>
            <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-6 cursor-pointer hover:bg-accent/40">
              <Upload className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{importFileName || 'Selecionar planilha .xlsx'}</span>
              <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={onImportFile} data-testid="clients-import-file" />
            </label>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setImportOpen(false)}>Cancelar</Button>
              <Button onClick={doImport} disabled={importing || !importFile} data-testid="clients-import-confirm">{importing ? 'Importando...' : 'Importar'}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="duplicates-dialog">
          <DialogHeader><DialogTitle>Clientes duplicados</DialogTitle></DialogHeader>
          {dupLoading ? (
            <p className="text-muted-foreground py-10 text-center">Carregando...</p>
          ) : dupGroups.length === 0 ? (
            <div className="py-12 text-center" data-testid="duplicates-empty">
              <Copy className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-3 text-muted-foreground">Nenhum cliente duplicado encontrado.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Selecione o cadastro que será <strong>mantido</strong> em cada grupo. Os demais serão mesclados — vendas e contratos são reatribuídos ao mantido, e os duplicados removidos.</p>
              {dupGroups.map((g, gi) => (
                <div key={gi} className="rounded-lg border p-4 space-y-3" data-testid={`duplicate-group-${gi}`}>
                  <RadioGroup value={keepChoice[gi]} onValueChange={(v) => setKeepChoice({ ...keepChoice, [gi]: v })} className="space-y-2">
                    {g.clients.map((c) => {
                      const kept = keepChoice[gi] === c.client_id;
                      return (
                        <label key={c.client_id} className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${kept ? 'border-primary bg-primary/5' : 'hover:bg-accent/40'}`}>
                          <RadioGroupItem value={c.client_id} className="mt-1" data-testid={`dup-keep-${c.client_id}`} />
                          <div className="flex-1 text-sm min-w-0">
                            <div className="font-medium flex items-center gap-2">{c.nome} {kept && <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)]">Manter</Badge>}</div>
                            <div className="text-muted-foreground text-xs truncate">CPF: {c.cpf || '-'} · Email: {c.email || '-'} · Tel: {c.telefone || '-'}</div>
                            <div className="text-xs text-muted-foreground">{c.sales_count} venda(s){c.has_access ? ' · acesso ao portal' : ''}{c.created_at ? ` · criado em ${formatDate(c.created_at)}` : ''}</div>
                          </div>
                        </label>
                      );
                    })}
                  </RadioGroup>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => mergeGroup(gi)} disabled={merging} data-testid={`merge-group-${gi}`}><GitMerge className="h-4 w-4 mr-2" /> {merging ? 'Mesclando...' : 'Mesclar grupo'}</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button type="button" variant="ghost" onClick={() => setDupOpen(false)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Users(props) { return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>; }
